import React from 'react';
import { Link } from 'react-router-dom';
import { Book, Clock, Award, FileEdit } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Card, CardContent, CardFooter, CardHeader } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Badge } from '@/components/ui/badge';

export interface CourseData {
  id: string;
  title: string;
  description: string;
  coverImage?: string;
  totalLessons: number;
  estimatedTime: string;
  progress?: number;
  completed?: boolean;
  status?: 'published' | 'draft';
}

interface CourseCardProps {
  course: CourseData;
  className?: string;
  compact?: boolean;
  isClickable?: boolean;
}

const CourseCard: React.FC<CourseCardProps> = ({ 
  course, 
  className, 
  compact = false,
  isClickable = true 
}) => {
  const {
    id,
    title,
    description,
    coverImage,
    totalLessons,
    estimatedTime,
    progress = 0,
    completed = false,
    status = 'published',
  } = course;

  const isDraft = status === 'draft';

  const cardContent = (
    <Card className={cn(
      "h-full overflow-hidden transition-all", 
      isClickable && "hover:shadow-glass-sm",
      completed && "border-green-100 bg-green-50/30",
      isDraft && "border-amber-100 bg-amber-50/30",
      className
    )}>
      <div className={cn(
        "w-full overflow-hidden relative",
        compact ? "aspect-[4/2]" : "aspect-video"
      )}>
        <img 
          src={coverImage || "/thumbnail.webp"} 
          alt={title} 
          className={cn(
            "h-full w-full object-cover",
            isClickable && "transition-transform duration-500 hover:scale-105",
            isDraft && "opacity-80"
          )}
        />
        {isDraft && (
          <Badge variant="secondary" className="absolute top-2 right-2 px-2 py-0.5 text-xs font-medium bg-amber-500 text-white">
            <FileEdit className="mr-1 h-3 w-3" /> Draft
          </Badge>
        )}
      </div>
      <CardHeader className={cn("pt-3 pb-0", compact && "p-3")}>
        <div className="flex items-center justify-between">
          <Badge variant="outline" className="px-2 py-0 text-xs font-normal">
            <Book className="mr-1 h-3 w-3" /> {totalLessons} Lessons
          </Badge>
          <Badge variant="outline" className="px-2 py-0 text-xs font-normal">
            <Clock className="mr-1 h-3 w-3" /> {estimatedTime}
          </Badge>
        </div>
        <h3 className={cn(
          "mt-2 font-semibold line-clamp-2", 
          compact ? "text-base" : "text-xl"
        )}>
          {title}
        </h3>
      </CardHeader>
      <CardContent className={cn("pb-4", compact && "py-1 px-3")}>
        <p className={cn(
          "text-muted-foreground line-clamp-2", 
          compact ? "text-xs" : "text-sm"
        )}>
          {description}
        </p>
      </CardContent>
      <CardFooter className={cn("pt-0", compact && "p-3")}>
        {isDraft ? (
          <div className="flex w-full items-center text-sm text-amber-600">
            <FileEdit className="mr-1 h-4 w-4" />
            <span>In development</span>
          </div>
        ) : completed ? (
          <div className="flex w-full items-center text-sm text-green-600">
            <Award className="mr-1 h-4 w-4" />
            <span>Completed</span>
          </div>
        ) : progress > 0 ? (
          <div className="w-full space-y-1">
            <div className="flex justify-between text-xs">
              <span className="text-muted-foreground">Progress</span>
              <span className="font-medium">{progress}%</span>
            </div>
            <Progress value={progress} className="h-1.5" />
          </div>
        ) : (
          <Badge variant="secondary" className="text-xs font-normal">
            {isClickable ? "Not started" : "Login to access"}
          </Badge>
        )}
      </CardFooter>
    </Card>
  );

  if (isClickable) {
    return (
      <Link to={`/course/${id}`} className="block transition-transform hover:scale-[1.02] focus:scale-[1.02] duration-300">
        {cardContent}
      </Link>
    );
  }

  return cardContent;
};

export default CourseCard;
